import React, { useMemo, useRef, useEffect, useState } from 'react';
import { useTasks } from '../../context/TasksContext';
import { useLeads } from '../../context/LeadsContext';
import { useRole } from '../../context/RoleContext';
import { useAuth } from '../../context/AuthContext';
import UnscheduledStagingColumn from './UnscheduledStagingColumn';
import EventCard from './WeekScheduler_EventCard';
import WeekSlot from './WeekScheduler_Slot';
import ScheduleJobModal from '../scheduler/ScheduleJobModal';
import { SLOT_HEIGHT, SLOTS_PER_DAY } from './calendarConstants';
import { isBoardVisibleTask } from '../../utils/buildForecast';

const timeSlots = Array.from({ length: SLOTS_PER_DAY }, (_, idx) => idx);

const formatTimeLabel = (idx) => {
  const hour24 = Math.floor(idx / 2);
  const minutes = idx % 2 === 0 ? 0 : 30;
  let hours = hour24 % 12;
  if (hours === 0) hours = 12;
  const ampm = hour24 >= 12 ? 'PM' : 'AM';
  const mm = minutes === 0 ? '00' : '30';
  return `${hours}:${mm} ${ampm}`;
};

// ── Same collision algorithm as WeekSchedulerView ────────────────────────────
function computeEventLayout(jobs) {
  if (!jobs.length) return new Map();
  const DEFAULT_MS = 3600000;
  const sorted = [...jobs].sort((a, b) => {
    const diff = new Date(a.scheduledStart).getTime() - new Date(b.scheduledStart).getTime();
    return diff || a.id.localeCompare(b.id);
  });
  const result = new Map();
  for (const job of sorted) {
    const startMs = new Date(job.scheduledStart).getTime();
    const endMs = job.scheduledEnd ? new Date(job.scheduledEnd).getTime() : startMs + DEFAULT_MS;
    const group = sorted.filter((o) => {
      const os = new Date(o.scheduledStart).getTime();
      const oe = o.scheduledEnd ? new Date(o.scheduledEnd).getTime() : os + DEFAULT_MS;
      return startMs < oe && endMs > os;
    });
    const groupSize = group.length;
    const colIdx = group.findIndex((g) => g.id === job.id);
    const start = new Date(job.scheduledStart);
    const startMins = start.getHours() * 60 + start.getMinutes();
    const endMins = job.scheduledEnd
      ? new Date(job.scheduledEnd).getHours() * 60 + new Date(job.scheduledEnd).getMinutes()
      : startMins + 60;
    const top = (startMins / 30) * SLOT_HEIGHT;
    const height = Math.max(1, (endMins - startMins) / 30) * SLOT_HEIGHT;
    const GAP = 2;
    const pct = 100 / groupSize;
    result.set(job.id, {
      top:    `${top}px`,
      height: `${height}px`,
      left:   `calc(${colIdx * pct}% + ${GAP}px)`,
      right:  'auto',
      width:  `calc(${pct}% - ${GAP * 2}px)`,
    });
  }
  return result;
}

const DaySchedulerView = ({ anchorDate, techFilter, hideUnscheduled = false }) => {
  const { tasks } = useTasks();
  const { leads } = useLeads();
  const { isTechnician } = useRole();
  const { user } = useAuth();

  const jobsFiltered = useMemo(() => {
    // Hide tasks the operator/cascade explicitly killed (cancelled / no-show).
    // Completed tasks remain visible as historical context.
    let jobs = (tasks || []).filter(
      (task) => task.type === 'job' && task.scheduledStart && isBoardVisibleTask(task),
    );
    if (isTechnician && user?.uid) {
      jobs = jobs.filter((task) => task.assignedTo === user.uid);
    }
    if (techFilter === 'unassigned') {
      jobs = jobs.filter((task) => !task.assignedTo);
    } else if (techFilter) {
      jobs = jobs.filter((task) => task.assignedTo === techFilter);
    }
    return jobs;
  }, [tasks, isTechnician, user?.uid, techFilter]);
  const jobsValidDate = useMemo(
    () =>
      jobsFiltered.filter((task) => {
        const d = new Date(task.scheduledStart);
        return !Number.isNaN(d.getTime());
      }),
    [jobsFiltered],
  );

  const dayStart = useMemo(() => {
    const d = new Date(anchorDate.getFullYear(), anchorDate.getMonth(), anchorDate.getDate());
    d.setHours(0, 0, 0, 0);
    return d;
  }, [anchorDate]);

  const isToday = useMemo(() => {
    const todayMidnight = new Date();
    todayMidnight.setHours(0, 0, 0, 0);
    return dayStart.getTime() === todayMidnight.getTime();
  }, [dayStart]);

  const scrollRef = useRef(null);
  const [now, setNow] = useState(() => new Date());
  const [slotModal, setSlotModal] = useState({ open: false, date: '', startTime: '' });

  // Scroll to current time on initial render (centered in view)
  useEffect(() => {
    if (!scrollRef.current) return;
    const el = scrollRef.current;
    requestAnimationFrame(() => {
      const n = new Date();
      const minutes = n.getHours() * 60 + n.getMinutes();
      const rawTop = (minutes / 30) * SLOT_HEIGHT;
      el.scrollTop = Math.max(0, rawTop - el.clientHeight / 3);
    });
  }, []);

  // Update current time every minute
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  const nowLineTop = useMemo(() => {
    const minutes = now.getHours() * 60 + now.getMinutes();
    return (minutes / 30) * SLOT_HEIGHT;
  }, [now]);

  const jobs = useMemo(() => {
    if (!tasks) return [];

    const jobsInRange = jobsValidDate.filter((task) => {
      const start = new Date(task.scheduledStart);
      return (
        start.getFullYear() === dayStart.getFullYear() &&
        start.getMonth() === dayStart.getMonth() &&
        start.getDate() === dayStart.getDate()
      );
    });

    if (process.env.NODE_ENV !== 'production') {
      console.debug('[Day Debug] jobsFiltered:', jobsFiltered.length, 'jobsValidDate:', jobsValidDate.length, 'jobsInRange:', jobsInRange.length);
    }

    return jobsInRange;
  }, [jobsValidDate, jobsFiltered.length, dayStart, tasks]);

  // Precompute collision-aware layout for the single day
  const dayLayout = useMemo(() => computeEventLayout(jobs), [jobs]);

  const handleSlotClick = (date, slotIdx) => {
    const d = date;
    const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const hour = Math.floor(slotIdx / 2);
    const minute = (slotIdx % 2) * 30;
    const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    setSlotModal({ open: true, date: dateStr, startTime: timeStr });
  };

  return (
    <>
    <div className="flex text-xs gap-4 mt-4 h-[calc(100vh-220px)]">
      {/* Left: calendar card */}
      <div className="flex-1 bg-white border border-gray-200 rounded-md flex">
        <div ref={scrollRef} className="flex-1 flex flex-col overflow-y-auto">
          {/* Header band — sticky so it stays visible when grid scrolls */}
          <div className="flex h-10 border-b border-gray-200 bg-gray-50 sticky top-0 z-10">
            <div className="w-20" />
            <div className="flex-1 flex items-center justify-center">
              <div className="text-xs font-semibold">
                {dayStart.toLocaleDateString(undefined, { weekday: 'short' })}
              </div>
              <div className="text-[11px] text-gray-600 ml-1">
                {dayStart.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
              </div>
            </div>
          </div>

          {/* Grid body */}
          <div className="flex flex-1">
            {/* Time axis */}
            <div className="w-20 border-r border-gray-200">
              {timeSlots.map((idx) => (
                <div
                  key={idx}
                  className="box-border border-b border-dashed border-gray-100 flex items-start justify-end pr-1 text-[11px] text-gray-500 bg-white"
                  style={{ height: SLOT_HEIGHT }}
                >
                  <span>{formatTimeLabel(idx)}</span>
                </div>
              ))}
            </div>

            {/* Day column - now with DnD support */}
            <div className="flex-1 relative text-[11px] select-none">
              {/* Jobs overlay */}
              <div className="absolute inset-0">
                {jobs.map((task) => {
                  const style = dayLayout.get(task.id) ?? {};
                  return (
                    <EventCard key={task.id} task={task} dayIndex={0} style={style} />
                  );
                })}
                {/* Current time indicator — only when viewing today */}
                {isToday && (
                  <div
                    style={{
                      position: 'absolute',
                      top: nowLineTop,
                      left: 0,
                      right: 0,
                      height: 2,
                      background: 'var(--cb-orange)',
                      zIndex: 20,
                      pointerEvents: 'none',
                    }}
                  />
                )}
              </div>

              {/* Slot rows with drop targets */}
              <div className="flex flex-col">
                {timeSlots.map((slotIdx) => (
                  <WeekSlot
                    key={slotIdx}
                    date={dayStart}
                    dayIndex={0}
                    slotIdx={slotIdx}
                    onSlotClick={handleSlotClick}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right: staging column — hidden for technicians */}
      {!hideUnscheduled && <UnscheduledStagingColumn leads={leads} tasks={tasks} />}
    </div>

    <ScheduleJobModal
      isOpen={slotModal.open}
      onClose={() => setSlotModal(s => ({ ...s, open: false }))}
      initialDate={slotModal.date}
      initialStartTime={slotModal.startTime}
    />
    </>
  );
};

export default DaySchedulerView;
